import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Input from '@mui/material/Input';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { ReactElement, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  useChecklessSettings,
  useIsTipEditablePostClose
} from 'reservations/account/providers/ConfigProvider';
import { ChecklessPaymentsDrawer } from 'reservations/checklessPayments/components/ChecklessPaymentsDrawer';
import {
  SELECT_GRATUITY_HEADER,
  TIP_NOT_ADJUSTABLE_SUBHEADER,
  TIP_SUBHEADER
} from 'reservations/checklessPayments/constants';
import { getDefaultTipValue } from 'reservations/checklessPayments/helpers';

type Props = {
  Button: (...props: any) => ReactElement;
  handleBack?: () => void;
  handleClick: () => void;
  handleSaveFailed?: () => void;
};

const TipModal = ({
  Button,
  handleBack,
  handleClick,
  handleSaveFailed
}: Props) => {
  const { control, setValue, watch } = useFormContext();
  const { tip_amounts: tipAmounts, default_tip: defaultTip } =
    useChecklessSettings()!;
  const tipPercentage = watch('tipPercentage');
  const { isCustom, tipValue } = getDefaultTipValue(
    tipPercentage ?? defaultTip,
    tipAmounts
  );
  const [showCustom, setShowCustom] = useState<boolean>(isCustom);

  const handleDeselectCustomTip = () => setShowCustom(false);
  const handleSelectCustomTip = () => setShowCustom(true);
  const isTipEditablePostClose = useIsTipEditablePostClose();

  const defaultTipRadios = tipAmounts
    .sort((a, b) => Number(a) - Number(b))
    .map(value => (
      <FormControlLabel
        key={value}
        value={value}
        control={<Radio />}
        label={`${value}%`}
        onChange={handleDeselectCustomTip}
      />
    ));

  const isValidNumericInput = (testValue: any) => !isNaN(Number(testValue));

  const disableButton =
    tipPercentage === '' ||
    tipPercentage === 'custom' ||
    !isValidNumericInput(tipPercentage) ||
    tipPercentage < 0 ||
    tipPercentage > 100;

  return (
    <ChecklessPaymentsDrawer
      headerText={SELECT_GRATUITY_HEADER}
      subheaderText={
        isTipEditablePostClose ? TIP_SUBHEADER : TIP_NOT_ADJUSTABLE_SUBHEADER
      }
      handleBack={handleBack}
    >
      <Box>
        <FormControl>
          <FormLabel id="tip-percentage-select">
            <Typography variant="h3">Gratuity</Typography>
          </FormLabel>
          <Controller
            name="tipPercentage"
            control={control}
            render={({ field: { onChange, name } }) => (
              <RadioGroup
                defaultValue={isCustom ? 'custom' : tipValue}
                aria-labelledby="tip-percentage-select-label"
                name={name}
                onChange={e => onChange(Number(e.target.value))}
              >
                {defaultTipRadios}
                <FormControlLabel
                  value="custom"
                  control={<Radio onChange={handleSelectCustomTip} />}
                  label="Custom"
                />
              </RadioGroup>
            )}
          />
          {showCustom && (
            <Box maxWidth="100px" marginTop="0.5rem">
              <Input
                autoFocus
                data-testid="tip-custom-input"
                type="text"
                inputMode="numeric"
                defaultValue={
                  isCustom && isValidNumericInput(tipPercentage)
                    ? tipPercentage
                    : ''
                }
                placeholder="0"
                sx={theme => ({
                  color: theme.typography.body1.color,
                  '&::before,': {
                    borderBottom: `1px solid ${theme.typography.body1.color}`
                  },
                  '&:hover:not(.Mui-disabled, .Mui-error):before': {
                    borderBottom: `2px solid ${theme.typography.body1.color}`
                  }
                })}
                onChange={e => {
                  if (isNaN(Number(e.target.value))) {
                    e.target.value = isValidNumericInput(tipPercentage)
                      ? tipPercentage
                      : '';
                  } else if (e.target.value.trim() == '') {
                    setValue('tipPercentage', '');
                    e.target.value = '';
                  } else {
                    setValue('tipPercentage', Number(e.target.value));
                  }
                }}
                endAdornment={<Typography>%</Typography>}
              />
            </Box>
          )}
        </FormControl>
      </Box>
      <Button
        disabled={disableButton}
        handleClick={handleClick}
        handleSaveFailed={handleSaveFailed}
      />
    </ChecklessPaymentsDrawer>
  );
};

export default TipModal;
